import {ApiClientError} from '@/api/client';
import {useCreatePlan, usePlan, useSetFeatures, useSetLimits, useUpdatePlan} from '@/hooks/usePlans';
import {useEffect, useState} from 'react';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {useNavigate, useParams} from 'react-router-dom';

const DEFAULT_LIMITS = [
    {key: 'sites', label: 'Newsletters', defaultError: 'Newsletter limit reached. Upgrade to create more newsletters.'},
    {key: 'staff', label: 'Staff Users', defaultError: 'Staff limit reached. Upgrade to add more team members.'},
    {key: 'members', label: 'Members', defaultError: 'Member limit reached. Upgrade for more members.'},
    {key: 'newsletters', label: 'Email newsletters', defaultError: 'Email newsletter limit reached.'},
    {key: 'storage_mb', label: 'Storage (MB)', defaultError: 'Storage limit reached. Upgrade for more space.'},
    {key: 'email_sends_monthly', label: 'Emails/Month', defaultError: 'Monthly email limit reached.'},
    {key: 'custom_integrations', label: 'Custom Integrations', defaultError: 'Integration limit reached.'}
];

const DEFAULT_FEATURES = [
    {key: 'custom_domain', label: 'Custom Domain', description: 'Use your own domain'},
    {key: 'remove_branding', label: 'Remove Branding', description: 'Hide TribeTale branding'},
    {key: 'paid_subscriptions', label: 'Paid Subscriptions', description: 'Accept payments from readers'},
    {key: 'advanced_analytics', label: 'Advanced Analytics', description: 'Detailed traffic & engagement data'},
    {key: 'api_access', label: 'API Access', description: 'Content API for custom integrations'},
    {key: 'import_export', label: 'Import/Export', description: 'Migrate content easily'},
    {key: 'white_label_emails', label: 'White-label Emails', description: 'Send from your domain'},
    {key: 'audit_logs', label: 'Audit Logs', description: 'Track all admin actions'}
];

interface FormData {
    name: string;
    slug: string;
    description: string;
    price_monthly_cents: number;
    price_yearly_cents: number;
    price_lifetime_cents: number;
    is_featured: boolean;
    is_active: boolean;
    is_public: boolean;
    badge_text: string;
    sort_order: number;
    paykickstart_product_id_monthly: string;
    paykickstart_product_id_yearly: string;
    paykickstart_product_id_lifetime: string;
}

function InputField({
    label,
    type = 'text',
    value,
    onChange,
    placeholder,
    hint,
    disabled,
    prefix,
    suffix,
    error
}: {
    label: string;
    type?: string;
    value: string | number;
    onChange: (newValue: string) => void;
    placeholder?: string;
    hint?: string;
    disabled?: boolean;
    prefix?: string;
    suffix?: string;
    error?: string;
}) {
    return (
        <>
            <style>{`
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
            `}</style>
            <div style={{marginBottom: 20}}>
                <label
                    style={{
                        display: 'block',
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: error ? '#ef4444' : 'var(--tt-text-secondary)',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px'
                    }}
                >
                    {label}
                </label>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {prefix && (
                        <span
                            style={{
                                height: 44,
                                padding: '0 12px',
                                display: 'flex',
                                alignItems: 'center',
                                background: 'var(--tt-table-header-bg)',
                                border: `1px solid ${error ? '#ef4444' : 'var(--tt-card-border)'}`,
                                borderRight: 'none',
                                borderRadius: '8px 0 0 8px',
                                fontSize: '1.4rem',
                                color: 'var(--tt-text-secondary)'
                            }}
                        >
                            {prefix}
                        </span>
                    )}
                    <input className="studio-field-control"
                        disabled={disabled}
                        placeholder={placeholder}
                        style={{
                            flex: 1,
                            height: 44,
                            padding: '0 14px',
                            borderRadius: prefix ? '0 8px 8px 0' : suffix ? '8px 0 0 8px' : 8,
                            border: `1px solid ${error ? '#ef4444' : 'var(--tt-card-border)'}`,
                            fontSize: '1.4rem',
                            color: 'var(--tt-text-primary)',
                            background: disabled ? 'var(--tt-table-header-bg)' : 'var(--tt-card-bg)',
                            fontFamily: 'inherit',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.15s ease',
                            opacity: disabled ? 0.6 : 1,
                            MozAppearance: type === 'number' ? 'textfield' : undefined
                        } as React.CSSProperties}
                        type={type}
                        value={value}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = error ? '#ef4444' : 'var(--tt-card-border)';
                        }}
                        onChange={e => onChange(e.target.value)}
                        onFocus={(e) => {
                            if (!disabled) {
                                e.currentTarget.style.borderColor = error ? '#ef4444' : 'var(--tt-accent)';
                            }
                        }}
                        onWheel={e => type === 'number' && e.currentTarget.blur()}
                    />
                    {suffix && (
                        <span
                            style={{
                                height: 44,
                                padding: '0 12px',
                                display: 'flex',
                                alignItems: 'center',
                                background: 'var(--tt-table-header-bg)',
                                border: `1px solid ${error ? '#ef4444' : 'var(--tt-card-border)'}`,
                                borderLeft: 'none',
                                borderRadius: '0 8px 8px 0',
                                fontSize: '1.4rem',
                                color: 'var(--tt-text-secondary)'
                            }}
                        >
                            {suffix}
                        </span>
                    )}
                </div>
                {error && (
                    <p style={{fontSize: '1.2rem', color: '#ef4444', margin: '6px 0 0 0', fontWeight: 500}}>
                        {error}
                    </p>
                )}
                {!error && hint && (
                    <p style={{fontSize: '1.2rem', color: 'var(--tt-text-tertiary)', margin: '6px 0 0 0'}}>
                        {hint}
                    </p>
                )}
            </div>
        </>
    );
}

function ToggleField({
    label,
    description,
    checked,
    onChange,
    error
}: {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (isChecked: boolean) => void;
    error?: string;
}) {
    return (
        <div>
            <label
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 0',
                    cursor: 'pointer'
                }}
            >
                <div
                    style={{
                        width: 44,
                        height: 24,
                        borderRadius: 12,
                        background: checked ? 'var(--tt-accent)' : 'var(--tt-card-border)',
                        position: 'relative',
                        flexShrink: 0,
                        transition: 'background 0.2s ease'
                    }}
                >
                    <div
                        style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#fff',
                            position: 'absolute',
                            top: 2,
                            left: checked ? 22 : 2,
                            transition: 'left 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}
                    />
                    <input
                        checked={checked}
                        style={{position: 'absolute', opacity: 0, width: 0, height: 0}}
                        type="checkbox"
                        onChange={e => onChange(e.target.checked)}
                    />
                </div>
                <div>
                    <div style={{fontSize: '1.4rem', fontWeight: 500, color: error ? '#ef4444' : 'var(--tt-text-primary)'}}>
                        {label}
                    </div>
                    {description && (
                        <div style={{fontSize: '1.2rem', color: 'var(--tt-text-secondary)', marginTop: 2}}>
                            {description}
                        </div>
                    )}
                </div>
            </label>
            {error && (
                <p style={{fontSize: '1.2rem', color: '#ef4444', margin: '6px 0 0 0', fontWeight: 500, paddingLeft: 32}}>
                    {error}
                </p>
            )}
        </div>
    );
}

function SectionCard({
    title,
    description,
    children
}: {
    title: string;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="studio-panel"
            style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, padding: 28, marginBottom: 24}}
        >
            <h2
                style={{fontSize: '1.6rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 4px 0', letterSpacing: '-.025em'}}
            >
                {title}
            </h2>
            {description && (
                <p
                    style={{
                        fontSize: '1.3rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 20px 0'
                    }}
                >
                    {description}
                </p>
            )}
            {!description && <div style={{marginTop: 20}} />}
            {children}
        </div>
    );
}

export function PlanEditorPage() {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;
    const isNew = !id || id === 'new';

    const {data: plan, isLoading} = usePlan(id);
    const createMutation = useCreatePlan();
    const updateMutation = useUpdatePlan();
    const setLimitsMutation = useSetLimits();
    const setFeaturesMutation = useSetFeatures();

    const [formData, setFormData] = useState<FormData>({
        name: '',
        slug: '',
        description: '',
        price_monthly_cents: 0,
        price_yearly_cents: 0,
        price_lifetime_cents: 0,
        is_featured: false,
        is_active: true,
        is_public: true,
        badge_text: '',
        sort_order: 1,
        paykickstart_product_id_monthly: '',
        paykickstart_product_id_yearly: '',
        paykickstart_product_id_lifetime: ''
    });

    const [limits, setLimitsState] = useState<Record<string, { value: number; error: string }>>({});
    const [features, setFeaturesState] = useState<Record<string, boolean>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (plan) {
            // console.log('[Plan Editor] Loading plan data:', plan);

            setFormData({
                name: plan.name,
                slug: plan.slug,
                description: plan.description || '',
                price_monthly_cents: plan.price_monthly_cents,
                price_yearly_cents: plan.price_yearly_cents,
                price_lifetime_cents: plan.price_lifetime_cents,
                is_featured: Boolean(plan.is_featured),
                is_active: Boolean(plan.is_active),
                is_public: Boolean(plan.is_public),
                badge_text: plan.badge_text || '',
                sort_order: plan.sort_order,
                paykickstart_product_id_monthly: plan.paykickstart_product_id_monthly || '',
                paykickstart_product_id_yearly: plan.paykickstart_product_id_yearly || '',
                paykickstart_product_id_lifetime: plan.paykickstart_product_id_lifetime || ''
            });

            const limitsMap: Record<string, { value: number; error: string }> = {};
            plan.limits?.forEach((l) => {
                limitsMap[l.limit_key] = {value: l.limit_value, error: l.error_message || ''};
            });
            setLimitsState(limitsMap);
            // console.log('[Plan Editor] Loaded limits:', limitsMap);

            const featuresMap: Record<string, boolean> = {};
            plan.features?.forEach((f) => {
                featuresMap[f.feature_key] = Boolean(f.is_enabled);
            });
            setFeaturesState(featuresMap);
            // console.log('[Plan Editor] Loaded features:', featuresMap);
        }
    }, [plan]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});

        try {
            if (isNew) {
                // console.log('[Plan Editor] Creating new plan with data:', { formData, limits, features });
                await createMutation.mutateAsync({
                    ...formData,
                    limits,
                    features
                });
            } else {
                // console.log('[Plan Editor] Updating plan:', id);
                // console.log('[Plan Editor] Form data:', formData);
                // console.log('[Plan Editor] Limits:', limits);
                // console.log('[Plan Editor] Features:', features);

                await updateMutation.mutateAsync({id: id!, data: formData});
                await setLimitsMutation.mutateAsync({id: id!, limits});
                await setFeaturesMutation.mutateAsync({id: id!, features});

                // console.log('[Plan Editor] All mutations completed successfully');
            }
            navigate('/superadmin/plans');
        } catch (err) {
            // console.error('[Plan Editor] Save failed:', err);
            // console.error('[Plan Editor] Error details:', err instanceof ApiClientError ? err.details : 'No details');
            if (err instanceof ApiClientError && err.details) {
                // console.error('[Plan Editor] Validation errors:', err.details);
                const errors: Record<string, string> = {};
                err.details.forEach((detail) => {
                    errors[detail.field] = detail.message;
                });
                setFieldErrors(errors);
            } else {
                setFieldErrors({general: err instanceof Error ? err.message : 'An error occurred'});
            }
        }
    };

    const isSaving = createMutation.isLoading || updateMutation.isLoading || setLimitsMutation.isLoading || setFeaturesMutation.isLoading;

    if (isLoading && !isNew) {
        return (
            <div className="studio-content-page" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
                <div className="studio-content-page"
                    style={{width: 40, height: 40, border: '3px solid var(--tt-card-border)', borderTopColor: 'var(--tt-accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div className="studio-content-page studio-admin-page studio-admin-plan-editor">
            {/* Header */}
            <header className="studio-admin-editor-heading">
                <button style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'none',
                    border: 'none',
                    color: 'var(--tt-text-secondary)',
                    fontSize: '1.3rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    padding: 0,
                    marginBottom: 16,
                    fontFamily: 'inherit'
                }}
                type="button"
                onClick={() => navigate('/superadmin/plans')}
                onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--tt-accent)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--tt-text-secondary)';
                }}
                >
                    <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                        <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Back to Plans
                </button>
                <p className="studio-eyebrow">Plan configuration</p>
                <h1>
                    {isNew ? 'Create Plan' : `Edit: ${plan?.name}`}
                </h1>
                <p className="studio-admin-intro">Define pricing, capacity, and capabilities for this subscription tier.</p>
            </header>

            {fieldErrors.general && (
                <div
                    style={{
                        padding: '14px 18px',
                        marginBottom: 24,
                        borderRadius: 12,
                        background: 'rgba(245, 11, 35, 0.08)',
                        border: '1px solid rgba(245, 11, 35, 0.2)',
                        color: 'var(--tt-red)',
                        fontSize: '1.4rem',
                        fontWeight: 600
                    }}
                >
                    {fieldErrors.general}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <SectionCard description="Core plan details and display settings" title="Basic Information">
                    <div style={{display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 20}}>
                        <InputField
                            error={fieldErrors['Plan Name']}
                            label="Plan Name"
                            placeholder="e.g., Pro"
                            value={formData.name}
                            onChange={v => setFormData({...formData, name: v})}
                        />
                        <InputField
                            disabled={!isNew}
                            error={fieldErrors.Slug}
                            hint={isNew ? 'URL-safe identifier (lowercase, no spaces)' : 'Cannot be changed after creation'}
                            label="Slug"
                            placeholder="e.g., pro"
                            value={formData.slug}
                            onChange={v => setFormData({...formData, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                        />
                    </div>

                    <InputField
                        error={fieldErrors.Description}
                        label="Description"
                        placeholder="A brief description of this plan"
                        value={formData.description}
                        onChange={v => setFormData({...formData, description: v})}
                    />

                    <div style={{display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 20}}>
                        <InputField
                            error={fieldErrors['Badge Text']}
                            hint="Displayed on pricing page"
                            label="Badge Text"
                            placeholder="e.g., Most Popular"
                            value={formData.badge_text}
                            onChange={v => setFormData({...formData, badge_text: v})}
                        />
                        <InputField
                            error={fieldErrors['Sort Order']}
                            hint="Lower numbers appear first"
                            label="Sort Order"
                            placeholder="1"
                            type="number"
                            value={formData.sort_order}
                            onChange={v => setFormData({...formData, sort_order: parseInt(v) || 1})}
                        />
                    </div>

                    <div style={{borderTop: '1px solid var(--tt-divider)', marginTop: 8, paddingTop: 8}}>
                        <ToggleField
                            checked={formData.is_featured}
                            description="Highlight this plan on the pricing page"
                            label="Featured"
                            onChange={v => setFormData({...formData, is_featured: v})}
                        />
                        <ToggleField
                            checked={formData.is_active}
                            description="Available for new subscriptions"
                            label="Active"
                            onChange={v => setFormData({...formData, is_active: v})}
                        />
                        <ToggleField
                            checked={formData.is_public}
                            description="Visible on public pricing page"
                            label="Public"
                            onChange={v => setFormData({...formData, is_public: v})}
                        />
                    </div>
                </SectionCard>

                <SectionCard description="Set prices in cents (e.g., 1900 = $19.00)" title="Pricing">
                    <div style={{display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr 1fr', gap: 20}}>
                        <div>
                            <InputField
                                error={fieldErrors['Monthly Price']}
                                label="Monthly Price"
                                placeholder="0"
                                suffix="¢"
                                type="number"
                                value={formData.price_monthly_cents || ''}
                                onChange={v => setFormData({...formData, price_monthly_cents: parseInt(v) || 0})}
                            />
                            <div style={{fontSize: '1.3rem', color: 'var(--tt-accent)', fontWeight: 600, marginTop: -12}}>
                                = ${(formData.price_monthly_cents / 100).toFixed(2)}/mo
                            </div>
                        </div>
                        <div>
                            <InputField
                                error={fieldErrors['Yearly Price']}
                                label="Yearly Price"
                                placeholder="0"
                                suffix="¢"
                                type="number"
                                value={formData.price_yearly_cents || ''}
                                onChange={v => setFormData({...formData, price_yearly_cents: parseInt(v) || 0})}
                            />
                            <div style={{fontSize: '1.3rem', color: 'var(--tt-accent)', fontWeight: 600, marginTop: -12}}>
                                = ${(formData.price_yearly_cents / 100).toFixed(2)}/yr
                            </div>
                        </div>
                        <div>
                            <InputField
                                error={fieldErrors['Lifetime Price']}
                                label="Lifetime Price"
                                placeholder="0"
                                suffix="¢"
                                type="number"
                                value={formData.price_lifetime_cents || ''}
                                onChange={v => setFormData({...formData, price_lifetime_cents: parseInt(v) || 0})}
                            />
                            <div style={{fontSize: '1.3rem', color: 'var(--tt-accent)', fontWeight: 600, marginTop: -12}}>
                                = ${(formData.price_lifetime_cents / 100).toFixed(2)}
                            </div>
                        </div>
                    </div>
                </SectionCard>

                <SectionCard description="Set usage limits for newsletters on this plan. Toggle 'Unlimited' for no restrictions." title="Limits">
                    <div>
                        {/* Column Headers */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: isCompact ? '1fr' : '160px 100px 100px 1fr',
                                gap: 16,
                                padding: '12px 0',
                                borderBottom: '2px solid var(--tt-divider)',
                                marginBottom: 8
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    color: 'var(--tt-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Limit Name
                            </div>
                            <div
                                style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    color: 'var(--tt-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Value
                            </div>
                            <div
                                style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    color: 'var(--tt-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Unlimited
                            </div>
                            <div
                                style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    color: 'var(--tt-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Error Message
                            </div>
                        </div>

                        {/* Limit Rows */}
                        <div style={{display: 'grid', gap: 12}}>
                            {DEFAULT_LIMITS.map((limit) => {
                                const isUnlimited = limits[limit.key]?.value === -1;
                                return (
                                    <div key={limit.key}

                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: isCompact ? '1fr' : '160px 100px 100px 1fr',
                                            gap: 16,
                                            alignItems: 'center',
                                            padding: '12px 0',
                                            borderBottom: '1px solid var(--tt-divider)'
                                        }}
                                    >
                                        <label
                                            style={{
                                                fontSize: '1.4rem',
                                                fontWeight: 500,
                                                color: 'var(--tt-text-primary)'
                                            }}
                                        >
                                            {limit.label}
                                        </label>
                                        <input className="studio-field-control"
                                            disabled={isUnlimited}
                                            placeholder={isUnlimited ? '∞' : '0'}
                                            style={{height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid var(--tt-input-border)', fontSize: '13px', color: isUnlimited ? 'var(--tt-text-tertiary)' : 'var(--tt-text-primary)', background: 'var(--tt-input-bg)', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', opacity: isUnlimited ? 0.6 : 1, cursor: isUnlimited ? 'not-allowed' : 'text'}}
                                            type="number"
                                            value={isUnlimited ? '' : (limits[limit.key]?.value || '')}
                                            onChange={e => setLimitsState({
                                                ...limits,
                                                [limit.key]: {
                                                    ...limits[limit.key],
                                                    value: parseInt(e.target.value) || 0
                                                }
                                            })
                                            }
                                            onWheel={e => e.currentTarget.blur()}
                                        />
                                        <label
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 44,
                                                    height: 24,
                                                    borderRadius: 12,
                                                    background: isUnlimited ? 'var(--tt-accent)' : 'var(--tt-card-border)',
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.2s ease'
                                                }}
                                                onClick={() => {
                                                    const newValue = isUnlimited ? 0 : -1;
                                                    setLimitsState({
                                                        ...limits,
                                                        [limit.key]: {
                                                            ...limits[limit.key],
                                                            value: newValue
                                                        }
                                                    });
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: '50%',
                                                        background: '#fff',
                                                        position: 'absolute',
                                                        top: 2,
                                                        left: isUnlimited ? 22 : 2,
                                                        transition: 'left 0.2s ease',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }}
                                                />
                                            </div>
                                            {isUnlimited && (
                                                <span style={{fontSize: '1.2rem', color: 'var(--tt-accent)', fontWeight: 600}}>
                                                    ∞
                                                </span>
                                            )}
                                        </label>
                                        <input className="studio-field-control"
                                            disabled={isUnlimited}
                                            placeholder={isUnlimited ? 'Not needed for unlimited' : `e.g., "${limit.label} limit reached. Upgrade for more!"`}
                                            style={{height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid var(--tt-input-border)', fontSize: '13px', color: isUnlimited ? 'var(--tt-text-tertiary)' : 'var(--tt-text-primary)', background: 'var(--tt-input-bg)', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', opacity: isUnlimited ? 0.6 : 1, cursor: isUnlimited ? 'not-allowed' : 'text'}}
                                            type="text"
                                            value={limits[limit.key]?.error ?? ''}
                                            onChange={e => setLimitsState({
                                                ...limits,
                                                [limit.key]: {...limits[limit.key], error: e.target.value}
                                            })
                                            }
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </SectionCard>

                <SectionCard description="Enable or disable features for this plan" title="Features">
                    <div style={{display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 0}}>
                        {DEFAULT_FEATURES.map(feature => (
                            <ToggleField
                                key={feature.key}
                                checked={features[feature.key] ?? false}
                                description={feature.description}
                                label={feature.label}
                                onChange={v => setFeaturesState({...features, [feature.key]: v})}
                            />
                        ))}
                    </div>
                </SectionCard>

                <SectionCard description="Paykickstart product IDs for checkout" title="Payment Integration">
                    <div style={{display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr 1fr', gap: 20}}>
                        <InputField
                            error={fieldErrors['PayKickstart Monthly Product ID']}
                            label="Monthly Product ID"
                            placeholder="pk_product_xxx"
                            value={formData.paykickstart_product_id_monthly}
                            onChange={v => setFormData({...formData, paykickstart_product_id_monthly: v})}
                        />
                        <InputField
                            error={fieldErrors['PayKickstart Yearly Product ID']}
                            label="Yearly Product ID"
                            placeholder="pk_product_xxx"
                            value={formData.paykickstart_product_id_yearly}
                            onChange={v => setFormData({...formData, paykickstart_product_id_yearly: v})}
                        />
                        <InputField
                            error={fieldErrors['PayKickstart Lifetime Product ID']}
                            label="Lifetime Product ID"
                            placeholder="pk_product_xxx"
                            value={formData.paykickstart_product_id_lifetime}
                            onChange={v => setFormData({...formData, paykickstart_product_id_lifetime: v})}
                        />
                    </div>
                </SectionCard>

                {/* Actions */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        marginTop: 8,
                        paddingBottom: 48
                    }}
                >
                    <button className="studio-control"
                        style={{height: 48, padding: '0 28px', borderRadius: 8, background: 'transparent', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease'}}
                        type="button"
                        onClick={() => navigate('/superadmin/plans')}
                    >
                        Cancel
                    </button>
                    <button className="studio-control studio-control-primary"
                        disabled={isSaving}
                        style={{cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, fontFamily: 'inherit'}}
                        type="submit"
                    >
                        {isSaving ? 'Saving...' : isNew ? 'Create Plan' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
}
